import {AxiosCall, CancelTokenSource} from '../axios';
import {serializeQueryParams} from './PaymentHistoryService';
import {ServiceSuccessResponseType} from './service-mappers/BaseMapper';
import {ArrayRows} from './service-mappers/ClinicServiceMapper';
import {ArrayResponse} from './service-mappers/CommonServiceMapper';

export const enrollForCCM = async (
  groupId: number,

  body: {
    patient_id: number;
    consent_type: string;
    notes: string;
    file: any;
  },
  options: any
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.postForm(
    `/clinic-group/${groupId}/ccm/patient/enroll?${queryParams}`,
    body
  );
};

//Start timer API service
export const startTimerCCM = async (
  groupId: number,
  ccmPatientId: number,
  body: {
    start_time: string;
  },
  options: any
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.post(
    `/clinic-group/${groupId}/ccm/patient/${ccmPatientId}/start-timer?${queryParams}`,
    body
  );
};

// Get CCM Log Reasons in the Stop Timer Dialog
export const getCCMLogReasons = async (groupId: number): Promise<any> => {
  return AxiosCall.get(`/clinic-group/${groupId}/ccm/log-reason`);
};

// Stop Timer for CCM Patient log record
export const updateStopTimerPatientLog = async (
  clinic_group_id: number,
  ccm_patient_id: number,
  ccm_patient_log_id: number | string | null,
  payload: {
    stop_time: string;
    time_spent: string;
    notes: string;
    reason: string;
    reason_id: number;
    log_type: string;
    follow_up_notes_due_date: string | null;
    follow_up_notes_assignee: number;
  },
  options: any
): Promise<any> => {
  const queryParams = serializeQueryParams(options);

  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/log/${ccm_patient_log_id}/stop-timer?${queryParams}`,
    payload
  );
};
// Cancel CCM Timer for CCM Patient log record
export const cancelCCMTimerPatientLog = async (
  clinic_group_id: number,
  ccm_patient_id: number,
  options?: {
    clinic_id?: number | string;
  }
): Promise<any> => {
  const queryParams = options?.clinic_id
    ? `clinic_id=${options.clinic_id}`
    : '';
  return AxiosCall.delete(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/cancel-timer?${queryParams}`
  );
};

// Get CCM PatientList to diplay in grid
export const getAllCCMPatientsList = async (
  clinic_group_id: number | string,
  options: any,
  cancelToken?: CancelTokenSource
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `clinic-group/${clinic_group_id}/ccm/patient/all?${queryParams}`,
    cancelToken
  );
};

/** Same filters as grid; returns file buffer for download (Excel). */
export const exportCCMPatientsList = async (
  clinic_group_id: number | string,
  options: any
): Promise<ArrayBuffer> => {
  const exportOptions = {...options, download: true};
  const queryParams = serializeQueryParams(exportOptions);
  return AxiosCall.getFileData(
    `clinic-group/${clinic_group_id}/ccm/patient/all?${queryParams}`
  );
};
// create CCM Plan for a patient
export const createCCMPlanforPatient = async (
  clinic_group_id: number | string,
  ccm_patient_id: number | undefined,
  planObject: any,
  options: any
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.post(
    `clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/plan?${queryParams}`,
    planObject
  );
};

// get API --> get the task type and task name for a patient for editing
export const getPatientCCMTaskDetails = async (
  clinic_group_id: string | number,
  ccm_patient_id: number,
  options?: {
    clinic_id?: string | number;
    is_external_care_plan?: boolean;
  }
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/plan?${queryParams}`
  );
};

export const updateCCMPlan = async (
  clinic_group_id: number | string,
  ccm_patient_id: number | string | undefined,
  updatedPlanObject: any,
  options: any
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/plan?${queryParams}`,
    updatedPlanObject
  );
};

export const deleteCCMPlan = async (
  clinic_group_id: number | string,
  ccm_patient_id: number,
  options?: {
    clinic_id?: string | number;
  }
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.delete(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/plan?${queryParams}`
  );
};

export const cancelCCMPlan = async (
  clinic_group_id: number | string,
  ccm_patient_id: number,
  options?: {
    clinic_id?: string | number;
  }
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.delete(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/cancel?${queryParams}`
  );
};

export const archiveCCMPatient = async (
  clinic_group_id: number | string,
  ccm_patient_id: number,
  options?: {
    clinic_id?: string | number;
  }
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/archive?${queryParams}`,
    {}
  );
};

export const unarchiveCCMPatient = async (
  clinic_group_id: number | string,
  ccm_patient_id: number,
  options?: {
    clinic_id?: string | number;
  }
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/un-archive?${queryParams}`,
    {}
  );
};

export const getAllCCMPatientLogDetails = async (
  clinic_group_id: number,
  ccm_patient_id: number,
  options: any,
  cancelToken?: CancelTokenSource
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/logs?${queryParams}`,
    cancelToken
  );
};

// Ingone log
export const ingnoreCCMLog = async (
  clinic_group_id: number,
  ccm_patient_id: number,
  ccm_patient_log_id: number,
  options: any,
  cancelToken?: CancelTokenSource
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.put(
    `clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/log/${ccm_patient_log_id}/ignore?${queryParams}`,
    cancelToken
  );
};

// create CCM Plan for a patient
export const createCCMRecordLogDetails = async (
  clinic_group_id: number | string,
  ccm_patient_id: number,
  body: {
    plan_log: Array<any>;
    patient_log_id: number;
  },
  options: any
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/plan-log?${queryParams}`,
    body
  );
};

export const getCCMPatientPlanLogRecordDetails = async (
  clinic_group_id: number,
  ccm_patient_id: number,
  ccm_patient_log_id: number,
  options?: {
    clinic_id?: string | number;
  }
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/log/${ccm_patient_log_id}/plan-logs?${queryParams}`
  );
};

// Add Timer for CCM Patient
export const addCCMTime = async (
  clinic_group_id: number | undefined,
  ccm_patient_id: number | undefined,
  payload: {
    start_time: string;
    stop_time: string;
    time_spent: number;
    notes: string;
    reason: string;
    reason_id: number;
    log_type: string;
    follow_up_notes_due_date: string | null;
    follow_up_notes_assignee: number;
  },
  options: any
): Promise<any> => {
  const queryParams = serializeQueryParams(options);

  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/add-time?${queryParams}`,
    payload
  );
};

export const getAllCCMPatientReports = async (
  clinic_group_id: number,
  options: any
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/ccm/report?${queryParams}`
  );
};

export const getCCMReportdetails = async (
  clinic_group_id: number,
  group_report_id: number,
  options: any
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  if (options?.download) {
    return AxiosCall.getFileData(
      `/clinic-group/${clinic_group_id}/ccm/report/${group_report_id}?${queryParams}`
    );
  }
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/ccm/report/${group_report_id}?${queryParams}`
  );
};

export const getCCMPatientDetails = async (
  clinic_group_id: number,
  ccm_patient_id: number,
  options?: {clinic_id?: number | string}
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}?${queryParams}`
  );
};

export const updatePrevFollowUpNotesLog = async (
  clinic_group_id: number,
  ccm_patient_id: number,
  ccm_patient_log_id: number,
  payload: {
    status: string;
  }
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/log/${ccm_patient_log_id}/mark-log-status`,
    payload
  );
};

export const getAllCCMUsersOfClinicGroup = async (
  group_id: number | string | null,
  options?: {
    clinic_id?: number | string;
  }
): Promise<any> => {
  const queryParams = options?.clinic_id
    ? `?clinic_id=${options.clinic_id}`
    : '';
  return AxiosCall.get(`/clinic-group/${group_id}/ccm/users/all${queryParams}`);
};

export const updateCCMAssignedUser = async (
  clinicGroupId: number,
  assigneeId: number,
  logId: number | null,
  ccmPatientId: number | null,
  dueDate: string,
  options?: {clinic_id?: number | string}
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  const url = `/clinic-group/${clinicGroupId}/patient/${ccmPatientId}/log/${logId}/follow-up-note-detail?${queryParams}`;
  const payload = {
    assignee_id: assigneeId,
    follow_up_due_date: dueDate
  };
  return AxiosCall.put(url, payload);
};

// create CCM Plan for a patient in outside of the system
export const createExternalCCMPlanforPatient = async (
  clinic_group_id: number | string,
  ccm_patient_id: number | undefined,
  options?: {
    clinic_id?: string | number;
  }
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.post(
    `clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/external-plan?${queryParams}`,
    {}
  );
};

export const updateCCMPatientDetails = async ({
  clinic_group_id,
  ccm_patient_id,
  payload,
  params
}: {
  clinic_group_id: number | string;
  ccm_patient_id: number | undefined;
  payload: {
    billing_provider: string | null;
    active_problems: any | null;
  };
  params?: {
    clinic_id?: string | number;
  };
}): Promise<any> => {
  const queryParams = serializeQueryParams(params);
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}?${queryParams}`,
    payload
  );
};

export const addPatientProblem = async ({
  clinic_group_id,
  payload,
  params
}: {
  clinic_group_id: number | string;
  payload: {
    patient_problem: string | null;
    icd10_code: string | null;
    is_chronic: boolean;
  };
  params?: {
    clinic_id?: string | number;
  };
}): Promise<any> => {
  const queryParams = serializeQueryParams(params);
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/ccm/patient-problem?${queryParams}`,
    payload
  );
};
export const getAllPatientProblems = async ({
  clinic_group_id,
  params
}: {
  clinic_group_id: number;
  params?: {
    search_text?: string;
    clinic_id?: number | string;
  };
}): Promise<any> => {
  const queryParams = serializeQueryParams(params);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/ccm/patient-problem/all${
      queryParams ? '?' + queryParams : ''
    }`
  );
};

export const fetchPatientProblems = async ({
  clinic_group_id,
  ccm_patient_id,
  params
}: {
  clinic_group_id: number;
  ccm_patient_id: number;
  params?: {
    clinic_id?: string | number;
  };
}): Promise<any> => {
  const queryParams = serializeQueryParams(params);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/ccm/patient-problems/${ccm_patient_id}?${queryParams}`
  );
};

export const resetCCMTimerService = async (
  clinic_group_id: number,
  ccm_patient_id: number,
  clinic_id?: number | string
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/reset-timer`,
    clinic_id ? {clinic_id: clinic_id} : {}
  );
};

export const markAsComplexCCMPatient = async (
  clinic_group_id: number,
  ccm_patient_id: number,
  clinic_id?: number | string
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/mark-as-complex`,
    clinic_id ? {clinic_id: clinic_id} : {}
  );
};

export const removeComplexCCMPatient = async (
  clinic_group_id: number,
  ccm_patient_id: number,
  clinic_id?: number | string
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/ccm/patient/${ccm_patient_id}/remove-complex`,
    clinic_id ? {clinic_id: clinic_id} : {}
  );
};
